import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { SpeciesDiagnosisDropdown } from 'src/app/medical-history/models/species-diagnosis-dropdown.model';
import { untilComponentDestroyed } from 'src/app/shared/helpers/rxjs.operator';
import { Languages } from 'src/app/shared/translation/languages.enum';

@Component({
  selector: 'diana-dropdown-diagnosis-keys',
  templateUrl: './dropdown-diagnosis-keys.component.html',
  styleUrls: ['./dropdown-diagnosis-keys.component.scss'],
  standalone: false
})
export class DropdownDiagnosisKeysComponent implements OnDestroy, OnInit {
  @Input() diagnosisList: Array<SpeciesDiagnosisDropdown> = [];
  @Input() formGroupDiagnosis!: FormGroup;
  @Input() diagnosis2List: Array<any> = [];
  @Input() isReadOnly = false;
  @Input() formArray!: AbstractControl<any, any, any>[];
  @Input() indexFormDiagnosis!: number;
  @Input() currentLanguage!: Languages;
  @Input() uniqueId: string | number = '';

  Languages = Languages;
  public diagnosis1List: Array<SpeciesDiagnosisDropdown> = [];

  constructor() {
  }

  ngOnInit(): void {
    this.initDropdowns();

    if (this.diagnosisList && this.diagnosisList.length > 0 && this.formGroupDiagnosis && this.formGroupDiagnosis.get('initialSelection')?.value) {
      const level2Data = this.diagnosisList.find(diagnosis => diagnosis.diagnosisId === this.formGroupDiagnosis.get('initialSelection')?.value);
      if (level2Data) {
        const level1Data = this.diagnosisList.find(diagnosis => diagnosis.codeLevel1 === level2Data.codeLevel1 && !diagnosis.codeLevel2 && !diagnosis.codeLevel3 && !diagnosis.codeLevel4);
        if (level1Data) {
          this.formGroupDiagnosis.get('diagnosisLevel1')?.setValue(level1Data.id);
          this.formGroupDiagnosis.get('diagnosisLevel1')?.markAsPristine();
          this.formGroupDiagnosis.get('diagnosisLevel2')?.setValue(level2Data.id);
          this.formGroupDiagnosis.get('diagnosisLevel2')?.markAsPristine();
          if (!this.isReadOnly) {
            this.formGroupDiagnosis.get('diagnosisLevel2')?.enable();
          }
        }
      }
    }
  }

  private initDropdowns(): void {
    if (this.formGroupDiagnosis) {
      if (!this.formGroupDiagnosis.get('diagnosisLevel1')?.value) {
        this.formGroupDiagnosis.get('diagnosisLevel2')?.disable();
      }
      this.diagnosis1List = this.generateDiagnosisLv1();

      if (this.formGroupDiagnosis) {
        // Handling the dropdown selection on code input
        this.formGroupDiagnosis.get('code')?.valueChanges
          .pipe(untilComponentDestroyed(this))
          .subscribe((code) => {
            const codeSplit = code ? code.split('.') : [];

            if (codeSplit.length >= 1 && codeSplit[0] && !isNaN(codeSplit[0])) {

              const selection1 = this.diagnosis1List.filter(
                diagnosis => diagnosis.codeLevel1?.toString() === codeSplit[0]
              );

              if (selection1 && selection1.length === 1) {

                this.formGroupDiagnosis.get('diagnosisLevel1')
                  ?.setValue(selection1[0].id);

                // Load level 2 options (async)
                this.updateDiagnosisLevel2(selection1[0].id || '');
                // Using setTimeout to fix the issue where it would only fill the first dropdown when pasting a complete code. 
                // As it was, it would only fill the two dropdowns by typing the code 
                setTimeout(() => {
                  if (codeSplit.length === 2 && codeSplit[1] && !isNaN(codeSplit[1])) {
                    const selection2 = this.diagnosis2List.filter(
                      diagnosis => diagnosis.codeLevel2.toString() === codeSplit[1]
                    );

                    if (selection2 && selection2.length === 1) {
                      this.formGroupDiagnosis.get('diagnosisLevel2')
                        ?.setValue(selection2[0].id);
                      this.formGroupDiagnosis.get('diagnosisLevel2')
                        ?.enable();
                    } else {
                      this.formGroupDiagnosis.get('diagnosisLevel2')
                        ?.setValue(undefined);
                      this.formGroupDiagnosis.get('diagnosisLevel2')
                        ?.disable();
                    }
                  } else {
                    this.formGroupDiagnosis.get('diagnosisLevel2')
                      ?.setValue(undefined);
                    this.formGroupDiagnosis.get('diagnosisLevel2')
                      ?.disable();
                  }
                }, 0);
              } 
            }
          });
      }

    }
  }

  ngOnDestroy(): void {
    untilComponentDestroyed(this);
  }

  handleDiagnosisLevel1Change(e: MatSelectChange): void {
    this.updateDiagnosisLevel2(e?.value);
    this.formGroupDiagnosis.get('code')?.setValue('', { emitEvent: false })
  }

  trackDiagnosis(index: number, diagnosis: SpeciesDiagnosisDropdown) {
    return diagnosis.id;
  }

  generateDiagnosisLv1(): Array<SpeciesDiagnosisDropdown> {
    let diagnosisLv1: Array<SpeciesDiagnosisDropdown> = this.diagnosisList.filter(diagnosis => diagnosis.codeLevel1 && !diagnosis.codeLevel2 && !diagnosis.codeLevel3 && !diagnosis.codeLevel4);
    // Checking if dignosisLv2 has remaining value related to the level 1, if not the level 1 is removed
    if (this.formArray.length > 1 && !this.formArray[this.indexFormDiagnosis].get('diagnosisLevel1')?.value) {
      const diagnosisLv2: Array<SpeciesDiagnosisDropdown> = this.diagnosisList.filter(diagnosis => diagnosis.codeLevel1 && diagnosis.codeLevel2 && !diagnosis.codeLevel3 && !diagnosis.codeLevel4);
      // checking level 2 values  related to level 1
      diagnosisLv1 = diagnosisLv1.filter(lv1 => {
        const existsDiagnosis2List = diagnosisLv2.filter(diagnosisInList => {
          const isDiagnosis2Select = this.formArray.length > 0 && this.formArray.find((diagnosis: any) => {
            return diagnosis.get('diagnosisLevel2').value && diagnosisInList?.id && diagnosis.get('diagnosisLevel2').value === diagnosisInList?.id
          });
          if (diagnosisInList.codeLevel2 && !diagnosisInList.codeLevel3 && !diagnosisInList.codeLevel4 && diagnosisInList.codeLevel1 === lv1.codeLevel1 && !isDiagnosis2Select) {
            return true;
          } else {
            return false;
          }
        });
        return existsDiagnosis2List && existsDiagnosis2List.length > 0 ? true : false;
      });
    }
    return diagnosisLv1;
  }

  updateDiagnosisLevel2(diagnosisLevel1Id: string): void {
    if (diagnosisLevel1Id) {
      this.formGroupDiagnosis.get('diagnosisLevel2')?.enable();
    } else {
      this.formGroupDiagnosis.get('diagnosisLevel2')?.setValue(undefined);
      this.formGroupDiagnosis.get('diagnosisLevel2')?.disable();
    }
  }

  generateDiagnosisLv2(): Array<SpeciesDiagnosisDropdown> {
    let diagnosisLv2: Array<SpeciesDiagnosisDropdown> = [];
    const diagnosisLevel1 = this.diagnosis1List.find(d => d.id == this.formGroupDiagnosis.get('diagnosisLevel1')?.value);
    if (diagnosisLevel1) {
      diagnosisLv2 = this.diagnosisList.filter(diagnosisInList => {
        const isDiagnosis2Select = this.formArray.length > 0 && this.formArray.find((diagnosis: any, index: number) => {
          return diagnosis.get('diagnosisLevel2').value && diagnosisInList?.id && diagnosis.get('diagnosisLevel2').value === diagnosisInList?.id.toString() && index !== this.indexFormDiagnosis
        });
        if (diagnosisInList.codeLevel2 && !diagnosisInList.codeLevel3 && !diagnosisInList.codeLevel4 && diagnosisInList.codeLevel1 === (diagnosisLevel1.codeLevel1) && !isDiagnosis2Select) {
          return true;
        } else {
          return false;
        }
      });
    }
    this.diagnosis2List = diagnosisLv2;
    return diagnosisLv2;
  }
}
